import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Box, Typography, MenuItem, Avatar, ListItemIcon, ListItemText, Menu, IconButton, Tooltip } from '@mui/material'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import AgoraUIKit, { layout } from 'amhm-vc-ui-kit'
import { styled } from '@mui/material/styles'
import { createGlobalStyle } from 'styled-components'
import ChamberVideoPlaceholder from './ChamberVideoPlaceholder'

// Common media URL prefix for profile pictures
const mediaUrlPrefix = 'https://media.aumhum.com/images/prod/'

const VideoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100vh',
  minWidth: '100%',
  minHeight: '100vh',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf4 50%, #f0f4ff 100%)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
}))

const HeroVideoWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'transparent',
}))

// Global styles for video card customization - SCOPED TO CHAMBER VIDEO ONLY
const VideoCardStyles = createGlobalStyle`
  /* IMPORTANT: All styles scoped to .chamber-video-container to prevent bleeding to other pages */
  
  /* Control button container auto-hide - targeting all possible variations */
  .chamber-video-container div[style*="position: absolute"][style*="bottom"][style*="left: 50%"],
  .chamber-video-container div[style*="position: absolute"][style*="bottom"][style*="justifyContent: center"],
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 24px"],
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 16px"],
  .chamber-video-container [class*="localBtnContainer"],
  .chamber-video-container div[style*="display: flex"][style*="justifyContent: center"][style*="position: absolute"][style*="bottom"] {
    transition: opacity 0.3s ease, visibility 0.3s ease !important;
  }
  
  .chamber-video-container.controls-hidden div[style*="position: absolute"][style*="bottom"][style*="left: 50%"],
  .chamber-video-container.controls-hidden div[style*="position: absolute"][style*="bottom"][style*="justifyContent: center"],
  .chamber-video-container.controls-hidden div[style*="position: absolute"][style*="bottom: 24px"],
  .chamber-video-container.controls-hidden div[style*="position: absolute"][style*="bottom: 16px"],
  .chamber-video-container.controls-hidden [class*="localBtnContainer"],
  .chamber-video-container.controls-hidden div[style*="display: flex"][style*="justifyContent: center"][style*="position: absolute"][style*="bottom"] {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
  
  /* More aggressive: Hide all bottom-positioned flex containers with buttons inside */
  .chamber-video-container.controls-hidden > div > div[style*="position: absolute"][style*="bottom"] > div[style*="display: flex"],
  .chamber-video-container.controls-hidden > div div[style*="position: absolute"][style*="bottom"][style*="z-index: 10000"],
  .chamber-video-container.controls-hidden > div div[style*="position: absolute"][style*="zIndex: 10000"] {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
  
  /* Focus button auto-hide */
  .chamber-video-container .focus-selection-button {
    transition: opacity 0.3s ease, visibility 0.3s ease !important;
  }
  
  .chamber-video-container.controls-hidden .focus-selection-button {
    opacity: 0 !important;
    visibility: hidden !important;
  }
  
  /* Make button container narrow - fit content only AND keep centered */
  .chamber-video-container [class*="localBtnContainer"],
  .chamber-video-container div[style*="display"][style*="flex"][style*="justifyContent: center"][style*="position: absolute"][style*="bottom: 24px"],
  .chamber-video-container div[style*="display: flex"][style*="position: absolute"][style*="bottom: 24px"][style*="left: 50%"],
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 24px"][style*="transform: translateX(-50%)"] {
    width: fit-content !important;
    min-width: auto !important;
    max-width: fit-content !important;
    display: flex !important;
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
  }
  
  /* Also narrow any parent wrappers */
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 24px"] > div[style*="display: flex"],
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 16px"] > div[style*="display: flex"] {
    width: fit-content !important;
    display: flex !important;
  }
  
  /* Catch-all: Any bottom-positioned container with buttons should be narrow and centered */
  .chamber-video-container div[style*="position: absolute"][style*="bottom"]:has(button) {
    width: fit-content !important;
    min-width: auto !important;
    display: flex !important;
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
  }
  
  /* Ensure buttons maintain their size - don't let inline-flex distort them */
  .chamber-video-container [class*="localBtnContainer"] button,
  .chamber-video-container div[style*="position: absolute"][style*="bottom"]:has(button) button {
    flex-shrink: 0 !important;
    flex-grow: 0 !important;
  }
  
  /* CRITICAL: Force centering of button strip - override all inline styles */
  .chamber-video-container div[style*="position"][style*="bottom"][style*="display"][style*="flex"]:has(button),
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 24px"]:has(button),
  .chamber-video-container div[style*="zIndex: 10000"]:has(button),
  .chamber-video-container div[style*="z-index: 10000"]:has(button),
  .chamber-video-container div[style*="bottom: 24px"][style*="zIndex: 10000"],
  .chamber-video-container div[style*="bottom: 24px"][style*="left: 50%"] {
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
    -webkit-transform: translateX(-50%) !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  /* Extra aggressive: Target any absolutely positioned element at bottom with flex display */
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 24px"][style*="display: flex"],
  .chamber-video-container div[style*="position: absolute"][style*="bottom: 24px"][style*="display:flex"] {
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
    width: fit-content !important;
  }
  
  /* Default: Ensure all video containers are visible - ONLY within chamber video */
  .chamber-video-container [data-uid] {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    background: rgba(103, 121, 227, 0.15) !important;
    border-radius: 16px !important;
    overflow: hidden !important;
    border: 3px solid rgba(103, 121, 227, 0.8) !important;
    box-shadow: 0 4px 20px rgba(103, 121, 227, 0.3), inset 0 0 0 1.5px rgba(255, 255, 255, 0.2) !important;
  }

  .chamber-video-container [data-uid] video {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
  }

  /* Fallback: If video doesn't have data-role yet, still show it */
  .chamber-video-container [data-uid]:not([data-role]) {
    position: relative !important;
    width: 100% !important;
    height: 100% !important;
    display: block !important;
    visibility: visible !important;
    background: rgba(103, 121, 227, 0.15) !important;
    border-radius: 16px !important;
    overflow: hidden !important;
    border: 3px solid rgba(103, 121, 227, 0.8) !important;
    box-shadow: 0 4px 20px rgba(103, 121, 227, 0.3), inset 0 0 0 1.5px rgba(255, 255, 255, 0.2) !important;
  }

  /* Hero video styling for mentor */
  .chamber-video-container [data-role="mentor"] {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    transform: none !important;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    min-width: 100% !important;
    min-height: 100% !important;
    z-index: 10 !important;
    border-radius: 0 !important;
    overflow: hidden !important;
    box-shadow: none !important;
    border: none !important;
    background: #000000 !important;
    backdrop-filter: none !important;
    display: block !important;
    visibility: visible !important;
  }

  .chamber-video-container [data-role="mentor"] video {
    border-radius: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
    visibility: visible !important;
  }

  .chamber-video-container [data-role="mentor"] [class*="username"],
  .chamber-video-container [data-role="mentor"] p,
  .chamber-video-container [data-role="mentor"] span {
    background: rgba(103, 121, 227, 0.85) !important;
    backdrop-filter: blur(16px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(16px) saturate(200%) !important;
    color: #ffffff !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 8px 16px !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 20px rgba(103, 121, 227, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5) !important;
    letter-spacing: 0.2px !important;
    /* Flexible width for mentor card names */
    position: absolute !important;
    top: 16px !important;
    left: 16px !important;
    margin: 0 !important;
    width: auto !important;
    max-width: calc(100% - 32px) !important;
    min-width: fit-content !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* Participant video cards - smaller, positioned at bottom right */
  .chamber-video-container [data-role="participant"] {
    position: absolute !important;
    width: 280px !important;
    height: 200px !important;
    border-radius: 16px !important;
    overflow: hidden !important;
    background: rgba(103, 121, 227, 0.2) !important;
    backdrop-filter: blur(20px) saturate(180%) !important;
    -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
    border: 3px solid rgba(103, 121, 227, 0.8) !important;
    box-shadow: 0 8px 32px rgba(103, 121, 227, 0.25), 0 4px 16px rgba(116, 80, 170, 0.2), inset 0 0 0 1.5px rgba(255, 255, 255, 0.2) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    z-index: 5 !important;
    display: block !important;
    visibility: visible !important;
    position: relative !important;
  }

  .chamber-video-container [data-role="participant"]:hover {
    transform: scale(1.05) translateY(-4px) !important;
    box-shadow: 0 12px 40px rgba(103, 121, 227, 0.35), 0 6px 20px rgba(116, 80, 170, 0.3) !important;
    border-color: rgba(103, 121, 227, 0.5) !important;
  }

  .chamber-video-container [data-role="participant"] video {
    border-radius: 16px !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
    visibility: visible !important;
  }

  .chamber-video-container [data-role="participant"] [class*="username"],
  .chamber-video-container [data-role="participant"] p,
  .chamber-video-container [data-role="participant"] span {
    background: rgba(103, 121, 227, 0.85) !important;
    backdrop-filter: blur(14px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(14px) saturate(200%) !important;
    color: #ffffff !important;
    font-size: 10px !important;
    font-weight: 600 !important;
    padding: 4px 10px !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 8px rgba(103, 121, 227, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5) !important;
    letter-spacing: 0.1px !important;
    /* Position at top left with flexible width */
    position: absolute !important;
    top: 8px !important;
    left: 8px !important;
    right: auto !important;
    bottom: auto !important;
    margin: 0 !important;
    /* Flexible width - expands to fit name but respects max-width */
    width: auto !important;
    max-width: calc(100% - 16px) !important;
    min-width: fit-content !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    line-height: 1.2 !important;
  }

  /* Status indicators (mic/camera) for small video cards (participant + local user) - positioned at bottom right */
  /* Target all small video cards - both participant role and local user cards */
  .chamber-video-container [data-role="participant"] > div[style*="position: absolute"][style*="top"],
  .chamber-video-container [data-role="participant"] > div[style*="position: absolute"][style*="right"],
  .chamber-video-container [data-role="participant"] > div > div[style*="position: absolute"][style*="top"],
  .chamber-video-container [data-role="participant"] > div > div[style*="position: absolute"][style*="right"],
  .chamber-video-container [data-role="participant"] > div[style*="position: absolute"]:has(svg[viewBox*="24"]),
  .chamber-video-container [data-role="participant"] div[style*="position: absolute"]:has(svg[viewBox*="0 0 24 24"]),
  .chamber-video-container [data-role="participant"] div[style*="position: absolute"][style*="display: flex"]:has(svg),
  /* Also target local user's small card (might not have data-role) */
  .chamber-video-container div[data-uid="0"] > div[style*="position: absolute"][style*="top"],
  .chamber-video-container div[data-uid="0"] > div[style*="position: absolute"][style*="right"],
  .chamber-video-container div[data-uid="0"] > div > div[style*="position: absolute"][style*="top"],
  .chamber-video-container div[data-uid="0"] > div > div[style*="position: absolute"][style*="right"],
  .chamber-video-container div[data-uid="0"] > div[style*="position: absolute"]:has(svg[viewBox*="24"]),
  .chamber-video-container div[data-uid="0"] div[style*="position: absolute"]:has(svg[viewBox*="0 0 24 24"]),
  /* Target any small video card (width < 400px) with status indicators */
  .chamber-video-container div[style*="position: absolute"][style*="width"]:not([data-role="mentor"]) > div[style*="position: absolute"]:has(svg[viewBox*="24"]) {
    background: rgba(0, 0, 0, 0.3) !important;
    backdrop-filter: blur(8px) saturate(150%) !important;
    -webkit-backdrop-filter: blur(8px) saturate(150%) !important;
    border-radius: 8px !important;
    padding: 4px 6px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    display: flex !important;
    gap: 4px !important;
    align-items: center !important;
    /* Position at BOTTOM RIGHT to maximize distance from name at top left */
    bottom: 8px !important;
    left: auto !important;
    right: 8px !important;
    top: auto !important;
    transform: none !important;
  }

  /* Style individual status icons in small cards - match mentor portal design */
  .chamber-video-container [data-role="participant"] > div[style*="position: absolute"] svg,
  .chamber-video-container [data-role="participant"] > div > div[style*="position: absolute"] svg,
  .chamber-video-container [data-role="participant"] div[style*="position: absolute"] svg[viewBox*="24"],
  .chamber-video-container [data-role="participant"] div[style*="position: absolute"] svg[viewBox*="0 0 24 24"],
  .chamber-video-container div[data-uid="0"] > div[style*="position: absolute"] svg,
  .chamber-video-container div[data-uid="0"] > div > div[style*="position: absolute"] svg,
  .chamber-video-container div[data-uid="0"] div[style*="position: absolute"] svg[viewBox*="24"] {
    width: 16px !important;
    height: 16px !important;
    color: #ffffff !important;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) !important;
  }

  /* Individual status icon boxes - match mentor portal colored boxes */
  /* Camera off icon - red background */
  .chamber-video-container [data-role="participant"] div[style*="position: absolute"]:has(svg[viewBox*="24"]):has(path[d*="M15 10"]),
  .chamber-video-container div[data-uid="0"] div[style*="position: absolute"]:has(svg[viewBox*="24"]):has(path[d*="M15 10"]) {
    /* Individual icon wrapper styling if needed */
  }

  /* Status indicators for mentor cards - keep at top center */
  .chamber-video-container [data-role="mentor"] > div[style*="position: absolute"][style*="top"],
  .chamber-video-container [data-role="mentor"] > div[style*="position: absolute"][style*="right"],
  .chamber-video-container [data-role="mentor"] > div > div[style*="position: absolute"][style*="top"],
  .chamber-video-container [data-role="mentor"] > div > div[style*="position: absolute"][style*="right"],
  .chamber-video-container [data-role="mentor"] > div[style*="position: absolute"]:has(svg[viewBox*="24"]),
  .chamber-video-container [data-role="mentor"] div[style*="position: absolute"]:has(svg[viewBox*="0 0 24 24"]),
  .chamber-video-container [data-role="mentor"] div[style*="position: absolute"][style*="display: flex"]:has(svg) {
    background: rgba(0, 0, 0, 0.3) !important;
    backdrop-filter: blur(8px) saturate(150%) !important;
    -webkit-backdrop-filter: blur(8px) saturate(150%) !important;
    border-radius: 8px !important;
    padding: 4px 6px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    display: flex !important;
    gap: 4px !important;
    align-items: center !important;
    /* Center horizontally at top for mentor cards */
    left: 50% !important;
    transform: translateX(-50%) !important;
    right: auto !important;
    top: 8px !important;
    bottom: auto !important;
  }

  /* Style individual status icons in mentor cards */
  .chamber-video-container [data-role="mentor"] > div[style*="position: absolute"] svg,
  .chamber-video-container [data-role="mentor"] > div > div[style*="position: absolute"] svg,
  .chamber-video-container [data-role="mentor"] div[style*="position: absolute"] svg[viewBox*="24"],
  .chamber-video-container [data-role="mentor"] div[style*="position: absolute"] svg[viewBox*="0 0 24 24"] {
    width: 16px !important;
    height: 16px !important;
    color: #ffffff !important;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) !important;
  }

  /* Position participant cards in a grid at bottom right, above control strip */
  .chamber-video-container [data-role="participant"]:nth-of-type(1) {
    bottom: 100px !important;
    right: 24px !important;
  }

  .chamber-video-container [data-role="participant"]:nth-of-type(2) {
    bottom: 100px !important;
    right: 320px !important;
  }

  .chamber-video-container [data-role="participant"]:nth-of-type(3) {
    bottom: 316px !important;
    right: 24px !important;
  }

  .chamber-video-container [data-role="participant"]:nth-of-type(4) {
    bottom: 316px !important;
    right: 320px !important;
  }

  /* Hide auditor and panelist roles */
  .chamber-video-container [data-role="auditor"],
  .chamber-video-container [data-role="panelist"] {
    display: none !important;
  }

  /* Keep RTC video visible while RTM metadata catches up.
     Chamber/kiosk joins can receive RTC media before RTM identity, and hiding the card
     creates a first-join race where the practitioner appears only after refresh. */
  .chamber-video-container [data-uid]:not([data-uid="0"]):not([data-has-rtm="true"]) {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  /* Show videos that have RTM data */
  .chamber-video-container [data-uid][data-has-rtm="true"] {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  /* Enable Agora action buttons - ensure they are clickable */
  .chamber-video-container div[style*="display: flex"][style*="justifyContent: center"] button,
  .chamber-video-container div[style*="display: flex"][style*="justifyContent: center"] > button,
  .chamber-video-container button[style*="borderRadius"],
  .chamber-video-container button[style*="width"][style*="height"],
  .chamber-video-container [class*="localBtnContainer"] button,
  .chamber-video-container [class*="localBtnContainer"] > button,
  .chamber-video-container div[style*="padding"][style*="display: flex"] button {
    pointer-events: auto !important;
    cursor: pointer !important;
    z-index: 10001 !important;
    position: relative !important;
  }

  /* Ensure button container allows pointer events on children */
  .chamber-video-container div[style*="display: flex"][style*="justifyContent: center"],
  .chamber-video-container div[style*="padding"][style*="display: flex"][style*="justifyContent: center"],
  .chamber-video-container [class*="localBtnContainer"] {
    pointer-events: none !important;
  }

  .chamber-video-container div[style*="display: flex"][style*="justifyContent: center"] > *,
  .chamber-video-container div[style*="padding"][style*="display: flex"][style*="justifyContent: center"] > *,
  .chamber-video-container [class*="localBtnContainer"] > * {
    pointer-events: auto !important;
  }
`

const ParticipantGrid = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '100px',
  right: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  zIndex: 5,
}))

export default function ModernChamberVideoInterface({
  agoraRtcProps,
  agoraRtmProps,
  agoraCallbacks,
  serverGivenNextAction,
  chamberName,
}) {
  const containerRef = useRef(null)
  const [uidToUsernameMap, setUidToUsernameMap] = useState({})
  const uidToUsernameMapRef = useRef({})
  const [remoteUsers, setRemoteUsers] = useState([])
  const remoteUsersRef = useRef([])
  // Track when each RTC user joined (for mapping RTM data to RTC UIDs)
  const rtcUserJoinTimesRef = useRef(new Map()) // Map<RTC_UID, timestamp>
  const [mentorUid, setMentorUid] = useState(null)
  const [focusMenuAnchor, setFocusMenuAnchor] = useState(null)
  
  // Auto-hide controls state
  const [showControls, setShowControls] = useState(true)
  const hideControlsTimeoutRef = useRef(null)
  const lastActivityTimeRef = useRef(Date.now())
  
  // Keep refs in sync with state
  useEffect(() => {
    uidToUsernameMapRef.current = uidToUsernameMap
    remoteUsersRef.current = remoteUsers
  }, [uidToUsernameMap, remoteUsers])
  
  // Force center the button container using JavaScript
  useEffect(() => {
    const centerButtonContainer = () => {
      console.log('[Button Container] Searching for Agora control strip...')
      
      if (containerRef.current) {
        // Find ALL divs
        const allDivs = containerRef.current.querySelectorAll('div')
        console.log(`[Button Container] Checking ${allDivs.length} div elements...`)
        
        let agoraStripFound = false
        
        // LOG ALL DIVS to see what's actually there
        console.log('[Button Container] === DUMPING ALL DIV STYLES ===')
        allDivs.forEach((div, idx) => {
          const computedStyle = window.getComputedStyle(div)
          const bottom = computedStyle.bottom
          const zIndex = computedStyle.zIndex
          
          // Log any div that might be relevant (has position absolute or non-auto bottom)
          if (computedStyle.position === 'absolute' || (bottom !== 'auto' && bottom !== '0px')) {
            console.log(`Div ${idx}:`, {
              position: computedStyle.position,
              bottom: bottom,
              left: computedStyle.left,
              zIndex: zIndex,
              display: computedStyle.display,
              width: computedStyle.width,
            })
          }
        })
        console.log('[Button Container] === END DUMP ===')
        
        allDivs.forEach((div, idx) => {
          const computedStyle = window.getComputedStyle(div)
          
          const position = computedStyle.position
          const zIndex = computedStyle.zIndex
          
          // Agora overrides our position:absolute to position:relative!
          // Look for relative OR absolute position with zIndex 10000
          const isAgoraContainer = 
            (position === 'relative' || position === 'absolute') && 
            (zIndex === '10000' || parseInt(zIndex) === 10000)
          
          if (isAgoraContainer) {
            console.log(`[Button Container] 🎯 FOUND AGORA STRIP! Div ${idx}:`, {
              position: computedStyle.position,
              bottom: computedStyle.bottom,
              left: computedStyle.left,
              width: computedStyle.width,
              zIndex: computedStyle.zIndex,
              display: computedStyle.display,
            })
            
            // Force center styling with maximum specificity
            div.style.setProperty('position', 'absolute', 'important')
            div.style.setProperty('left', '50%', 'important')
            div.style.setProperty('right', 'auto', 'important')
            div.style.setProperty('transform', 'translateX(-50%)', 'important')
            div.style.setProperty('-webkit-transform', 'translateX(-50%)', 'important')
            div.style.setProperty('width', 'fit-content', 'important')
            div.style.setProperty('max-width', 'fit-content', 'important')
            div.style.setProperty('min-width', 'auto', 'important')
            div.style.setProperty('margin-left', '0', 'important')
            div.style.setProperty('margin-right', '0', 'important')
            
            console.log('[Button Container] ✅ Centering applied! Changed position to absolute and centered.')
            
            agoraStripFound = true
          }
        })
        
        if (!agoraStripFound) {
          console.log('[Button Container] ⚠️ No suitable candidates found!')
        }
      }
    }
    
    // Apply multiple times with different delays
    console.log('[Button Container] Setting up centering for Agora strip...')
    centerButtonContainer()
    
    const timer1 = setTimeout(centerButtonContainer, 500)
    const timer2 = setTimeout(centerButtonContainer, 1000)
    const timer3 = setTimeout(centerButtonContainer, 2000)
    const timer4 = setTimeout(centerButtonContainer, 3000)
    const timer5 = setTimeout(centerButtonContainer, 5000)
    
    // Set up a continuous interval to keep re-applying (in case Agora overwrites)
    const interval = setInterval(centerButtonContainer, 2000)
    
    // Set up a mutation observer to re-apply when DOM changes
    const observer = new MutationObserver(() => {
      centerButtonContainer()
    })
    
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }
    
    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
      clearTimeout(timer4)
      clearTimeout(timer5)
      clearInterval(interval)
      observer.disconnect()
    }
  }, [])
  
  // Debug: Log map state changes for remote users
  useEffect(() => {
    const mapKeys = Object.keys(uidToUsernameMap)
    const localUidNum = agoraRtcProps?.uid !== undefined ? parseInt(agoraRtcProps.uid) : null
    
    const remoteEntries = mapKeys
      .filter(key => {
        const uidNum = parseInt(key)
        return !isNaN(uidNum) && (localUidNum === null || uidNum !== localUidNum)
      })
      .map(key => {
        const value = uidToUsernameMap[key]
        return {
          key,
          keyType: typeof key,
          value: value ? (value.length < 100 ? value : value.substring(0, 100) + '...') : 'null',
          valueType: typeof value,
          isJson: value && (value.includes('{') || value.includes('"name"')),
        }
      })
    
    console.log('🔵 [REMOTE USER FLOW] uidToUsernameMap state changed:', {
      totalKeys: mapKeys.length,
      allKeys: mapKeys,
      localUid: localUidNum,
      remoteEntries,
      remoteEntryCount: remoteEntries.length,
      fullMap: Object.entries(uidToUsernameMap).reduce((acc, [k, v]) => {
        acc[k] = typeof v === 'string' && v.length < 100 ? v : (typeof v === 'string' ? v.substring(0, 100) + '...' : '...')
        return acc
      }, {}),
    })
  }, [uidToUsernameMap, agoraRtcProps?.uid])
  const focusMenuOpen = Boolean(focusMenuAnchor)
  
  // Auto-hide controls after 10 seconds of inactivity
  useEffect(() => {
    const handleUserActivity = () => {
      const now = Date.now()
      const timeSinceLastActivity = now - lastActivityTimeRef.current
      
      // Throttle: Only process if at least 1000ms has passed since last activity
      if (timeSinceLastActivity < 1000) {
        return
      }
      
      lastActivityTimeRef.current = now
      setShowControls(true)
      
      // Clear any existing timer
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current)
        hideControlsTimeoutRef.current = null
      }
      
      // Set new hide timer
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false)
        hideControlsTimeoutRef.current = null
      }, 10000) // Hide after 10 seconds of inactivity
    }
    
    // Start the initial timer
    hideControlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false)
      hideControlsTimeoutRef.current = null
    }, 10000)
    
    // Add event listeners for mouse move and touch
    window.addEventListener('mousemove', handleUserActivity)
    window.addEventListener('touchstart', handleUserActivity)
    window.addEventListener('touchmove', handleUserActivity)
    
    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleUserActivity)
      window.removeEventListener('touchstart', handleUserActivity)
      window.removeEventListener('touchmove', handleUserActivity)
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current)
        hideControlsTimeoutRef.current = null
      }
    }
  }, [])
  
  // Helper to parse RTM username
  const parseRtmUsername = (username) => {
    if (!username || typeof username !== 'string' || username.trim() === '') {
      return { role: null, name: 'Unknown', profilePicId: null }
    }
    try {
      const parsed = JSON.parse(username)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { role: null, name: username, profilePicId: null }
      }
      return {
        role: parsed.role || null,
        name: parsed.name || 'Unknown',
        profilePicId: parsed.profilePicId || null,
      }
    } catch (error) {
      return { role: null, name: username, profilePicId: null }
    }
  }
  
  // Helper to get profile picture URL
  const getProfilePictureUrl = (profilePicId) => {
    if (!profilePicId) return null
    if (profilePicId.startsWith('http://') || profilePicId.startsWith('https://')) {
      return profilePicId
    }
    // Use common prefix with single slash
    return `${mediaUrlPrefix}${profilePicId}`
  }
  
  // Helper to get initials
  const getInitials = (name) => {
    if (!name || name === 'Me') return 'Me'
    
    // Don't show initials for loading/unknown states
    if (name === 'Loading...' || name === 'Guest' || name === 'Unknown') {
      return 'U'
    }
    
    const trimmedName = name.trim()
    const words = trimmedName.split(/\s+/).filter(w => w.length > 0)
    
    if (words.length > 1) {
      // Multiple words: use first letter of first and last word
      return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    } else if (trimmedName.length > 0) {
      // Single word: use first letter
      return trimmedName.charAt(0).toUpperCase()
    }
    
    return 'U'
  }

  // Parse RTM usernames to identify mentor role
  useEffect(() => {
    const parseUsernames = () => {
      const mentorUids = []
      
      // Check local user
      if (agoraRtcProps?.uid !== undefined && agoraRtmProps?.username) {
        try {
          const parsed = JSON.parse(agoraRtmProps.username)
          if (parsed?.role === 'mentor') {
            mentorUids.push(agoraRtcProps.uid)
          }
        } catch (e) {
          // Not JSON, skip
        }
      }
      
      // Check remote users
      Object.keys(uidToUsernameMap).forEach((uid) => {
        const username = uidToUsernameMap[uid]
        if (!username) return
        
        try {
          const parsed = JSON.parse(username)
          if (parsed?.role === 'mentor') {
            mentorUids.push(parseInt(uid))
          }
        } catch (e) {
          // Not JSON, skip
        }
      })
      
      if (mentorUids.length > 0) {
        setMentorUid(mentorUids[0]) // Use first mentor found
      }
    }
    
    parseUsernames()
  }, [agoraRtcProps?.uid, agoraRtmProps?.username, uidToUsernameMap])

  // Add local user's RTM username to the map
  useEffect(() => {
    if (agoraRtcProps?.uid !== undefined && agoraRtmProps?.username) {
      const uid = agoraRtcProps.uid
      const uidNum = parseInt(uid)
      const uidStr = uid.toString()
      
      console.log('Adding local user to map:', {
        uid,
        uidNum,
        uidStr,
        username: agoraRtmProps.username,
      })
      
      setUidToUsernameMap((prev) => ({
        ...prev,
        [uid]: agoraRtmProps.username, // Store with original format
        [uidNum]: agoraRtmProps.username, // Store with number key
        [uidStr]: agoraRtmProps.username, // Store with string key
        // Also store with 0 for local user (uid 0)
        0: agoraRtmProps.username,
        '0': agoraRtmProps.username,
      }))
    }
  }, [agoraRtcProps?.uid, agoraRtmProps?.username])

  // Enhanced callbacks to track users and usernames
  const enhancedCallbacks = {
    ...agoraCallbacks,
    'user-joined': (user) => {
      // Track join time for this RTC user (for mapping RTM data later)
      const rtcUid = user.uid
      const rtcUidNum = typeof rtcUid === 'string' ? parseInt(rtcUid, 10) : Number(rtcUid)
      rtcUserJoinTimesRef.current.set(rtcUid, Date.now())
      rtcUserJoinTimesRef.current.set(rtcUidNum, Date.now())
      rtcUserJoinTimesRef.current.set(String(rtcUid), Date.now())
      
      setRemoteUsers((prev) => {
        const exists = prev.find((u) => u.uid === user.uid)
        if (exists) return prev
        return [...prev, user]
      })
      
      // Try to extract username from DOM immediately for this user
      // This provides immediate feedback while waiting for RTM data
      setTimeout(() => {
        try {
          const videoCard = containerRef.current?.querySelector(`[data-uid="${rtcUid}"]`)
          if (videoCard) {
            // Look for name pill
            const namePillSelectors = [
              'p[style*="background"]',
              'span[style*="background"]',
              'div[style*="background"][style*="position"]',
              '[class*="username"]',
            ]
            
            for (const selector of namePillSelectors) {
              const elements = videoCard.querySelectorAll(selector)
              for (const el of elements) {
                const text = (el.textContent || el.innerText || '').trim()
                // If it's a valid name (not JSON, not "User {uid}", etc.)
                if (text && 
                    text.length > 0 && 
                    text.length < 100 &&
                    !text.includes('{') && 
                    !text.includes('"name"') && 
                    !text.includes('"role"') &&
                    text !== `User ${rtcUid}` &&
                    text !== 'Loading...' &&
                    text !== 'Unknown' &&
                    text !== 'Guest' &&
                    !text.match(/^\d+$/)) {
                  // Build RTM username JSON from the name we found
                  const rtmUsername = JSON.stringify({
                    role: 'mentor', // Default role, will be updated when RTM data arrives
                    name: text,
                    profilePicId: null,
                  })
                  
                  // Update the map with this name immediately
                  setUidToUsernameMap((prev) => {
                    // Only update if we don't already have JSON data for this UID
                    const currentValue = prev[rtcUid] || prev[rtcUidNum] || prev[String(rtcUid)]
                    const hasJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    
                    if (!hasJson) {
                      console.log('🟢 [USER-JOINED] Updating map from DOM name pill:', {
                        rtcUid,
                        name: text,
                      })
                      return {
                        ...prev,
                        [rtcUid]: rtmUsername,
                        [rtcUidNum]: rtmUsername,
                        [String(rtcUid)]: rtmUsername,
                      }
                    }
                    return prev
                  })
                  return // Found name, stop searching
                }
              }
            }
          }
        } catch (e) {
          console.warn('Error extracting name from DOM on user-joined:', e)
        }
      }, 1000) // Wait 1 second for DOM to render
      
      agoraCallbacks?.['user-joined']?.(user)
    },
    'user-left': (user) => {
      // Clean up join time tracking
      const rtcUid = user.uid
      const rtcUidNum = typeof rtcUid === 'string' ? parseInt(rtcUid, 10) : Number(rtcUid)
      rtcUserJoinTimesRef.current.delete(rtcUid)
      rtcUserJoinTimesRef.current.delete(rtcUidNum)
      rtcUserJoinTimesRef.current.delete(String(rtcUid))
      
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid))
      agoraCallbacks?.['user-left']?.(user)
    },
    'rtm-user-joined': (uid, username) => {
      // Store with both string and number keys to ensure we can find it
      const uidNum = parseInt(uid)
      const uidStr = uid.toString()
      const isLocalUser = agoraRtcProps?.uid !== undefined && parseInt(uid) === parseInt(agoraRtcProps.uid)
      
      console.log('🔵 [REMOTE USER FLOW] === RTM USER JOINED CALLBACK FIRED ===', {
        uid,
        uidType: typeof uid,
        uidNum,
        uidStr,
        username,
        usernameType: typeof username,
        usernameLength: username?.length,
        isLocalUser,
        isRemoteUser: !isLocalUser,
        localUid: agoraRtcProps?.uid,
        usernamePreview: username?.substring(0, 100),
        isJson: username && (username.includes('{') || username.includes('"name"')),
      })
      
      setUidToUsernameMap((prev) => {
        const currentValue = prev[uid] || prev[uidNum] || prev[uidStr]
        // Always update if we receive JSON from RTM, even if a plain string exists
        // This ensures we have the complete RTM data (including profilePicId, role, etc.)
        const isJson = username && (username.includes('{') || username.includes('"name"'))
        const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
        
        // Update if: not set, or we received JSON and current is not JSON, or JSON is different
        const shouldUpdate = !currentValue || 
          (isJson && !currentIsJson) ||
          (isJson && currentValue !== username)
        
        console.log('🔵 [REMOTE USER FLOW] rtm-user-joined - Map update decision:', {
          uid,
          isRemoteUser: !isLocalUser,
          currentValue: currentValue ? (currentValue.length < 100 ? currentValue : currentValue.substring(0, 100) + '...') : 'null',
          currentIsJson,
          isJson,
          shouldUpdate,
          prevMapKeys: Object.keys(prev),
          prevMapSize: Object.keys(prev).length,
        })
        
        if (shouldUpdate) {
          // CRITICAL: Map RTM ID to RTC UID
          // The rtm-user-joined callback receives RTM IDs, but video components use RTC UIDs
          // We need to also store usernames with RTC UIDs from remoteUsers
          const updated = {
            ...prev,
            [uid]: username, // Store with RTM ID (original format)
            [uidNum]: username, // Store with RTM ID (number key)
            [uidStr]: username, // Store with RTM ID (string key)
          }
          
          // CRITICAL: Map RTM username to RTC UIDs
          // Strategy:
          // 1. First, try exact match (RTM ID === RTC UID) - this is most reliable
          // 2. If no exact match, map to ALL RTC users without a username (they might share the same RTM username)
          // Use ref to get latest remoteUsers (state might be stale in callback)
          const currentRemoteUsers = remoteUsersRef.current || []
          
          // First, try exact match: if RTM ID equals any RTC UID, use that
          let exactMatchFound = false
          for (const remoteUser of currentRemoteUsers) {
            const rtcUid = remoteUser.uid
            const rtcUidNum = typeof rtcUid === 'string' ? parseInt(rtcUid, 10) : Number(rtcUid)
            const rtcUidStr = String(rtcUid)
            
            // Check if RTM ID matches RTC UID exactly
            if ((!isNaN(uidNum) && (uidNum === rtcUidNum || uidStr === rtcUidStr)) ||
                (uid === rtcUid || uid === rtcUidNum || uid === rtcUidStr)) {
              updated[rtcUid] = username
              updated[rtcUidNum] = username
              updated[rtcUidStr] = username
              exactMatchFound = true
              console.log('🟢 [RTM-USER-JOINED] Exact match found:', { rtmUid: uid, rtcUid })
              break
            }
          }
          
          // If no exact match, try to map to ALL remote users without usernames
          // This handles cases where RTM UID doesn't match RTC UID but we know the username
          if (!exactMatchFound && !isNaN(uidNum) && uidNum !== 0 && !isLocalUserRtmData) {
            for (const remoteUser of currentRemoteUsers) {
              const rtcUid = remoteUser.uid
              const rtcUidNum = typeof rtcUid === 'string' ? parseInt(rtcUid, 10) : Number(rtcUid)
              const rtcUidStr = String(rtcUid)
              
              // Skip local user
              if (rtcUid === 0 || (agoraRtcProps?.uid !== undefined && rtcUid === agoraRtcProps.uid)) {
                continue
              }
              
              // Check if this RTC user doesn't have a username yet
              const hasUsername = updated[rtcUid] || updated[rtcUidNum] || updated[rtcUidStr] || 
                                 prev[rtcUid] || prev[rtcUidNum] || prev[rtcUidStr]
              
              if (!hasUsername) {
                // Map this RTM username to this RTC UID
                updated[rtcUid] = username
                updated[rtcUidNum] = username
                updated[rtcUidStr] = username
                console.log('🟢 [RTM-USER-JOINED] Mapped RTM username to RTC UID (no existing username):', {
                  rtmUid: uid,
                  rtcUid,
                  usernamePreview: username.length < 50 ? username : username.substring(0, 50) + '...',
                })
                exactMatchFound = true
                break // Only map to the first user without a username to avoid duplicates
              }
            }
          }
          
          // If no exact match, use timing-based mapping (only for remote users, not local)
          // Also check if this RTM data is for the local user - if so, don't map to remote RTC UIDs
          const isLocalUserRtmData = agoraRtcProps?.uid !== undefined && 
                                   (uidNum === agoraRtcProps.uid || 
                                    uidStr === String(agoraRtcProps.uid) ||
                                    uid === agoraRtcProps.uid)
          
          if (!exactMatchFound && !isNaN(uidNum) && uidNum !== 0 && !isLocalUserRtmData) {
            // Find RTC users without usernames, sorted by join time (most recent first)
            // Exclude local user (uid === 0 or matches agoraRtcProps.uid)
            const usersWithoutUsername = currentRemoteUsers
              .filter((remoteUser) => {
                const rtcUid = remoteUser.uid
                // Skip local user
                if (rtcUid === 0 || (agoraRtcProps?.uid !== undefined && rtcUid === agoraRtcProps.uid)) {
                  return false
                }
                
                const rtcUidNum = typeof rtcUid === 'string' ? parseInt(rtcUid, 10) : Number(rtcUid)
                const rtcUidStr = String(rtcUid)
                const hasUsername = updated[rtcUid] || updated[rtcUidNum] || updated[rtcUidStr] || 
                                   prev[rtcUid] || prev[rtcUidNum] || prev[rtcUidStr]
                return !hasUsername
              })
            .map((remoteUser) => ({
              user: remoteUser,
              joinTime: rtcUserJoinTimesRef.current.get(remoteUser.uid) || 
                       rtcUserJoinTimesRef.current.get(Number(remoteUser.uid)) || 
                       rtcUserJoinTimesRef.current.get(String(remoteUser.uid)) || 
                       0
            }))
            .sort((a, b) => b.joinTime - a.joinTime) // Most recent first
          
            // Map RTM username to the most recently joined RTC user without a username
            // This assumes RTM data arrives shortly after RTC join
            if (usersWithoutUsername.length > 0) {
              const mostRecentUser = usersWithoutUsername[0].user
              const rtcUid = mostRecentUser.uid
              const rtcUidNum = typeof rtcUid === 'string' ? parseInt(rtcUid, 10) : Number(rtcUid)
              const rtcUidStr = String(rtcUid)
              
              updated[rtcUid] = username
              updated[rtcUidNum] = username
              updated[rtcUidStr] = username
            }
          }
          
          console.log('🟢 [REMOTE USER FLOW] ✅ UPDATED uidToUsernameMap from rtm-user-joined', {
            uid,
            isRemoteUser: !isLocalUser,
            newKeys: Object.keys(updated),
            newMapSize: Object.keys(updated).length,
            storedValue: username.length < 100 ? username : username.substring(0, 100) + '...',
            allKeys: Object.keys(updated),
            allValues: Object.values(updated).map(v => {
              if (typeof v === 'string' && v.length < 100) return v
              if (typeof v === 'string') return v.substring(0, 100) + '...'
              return '...'
            }),
          })
          
          // Update DOM to show videos that now have RTM data
          setTimeout(() => {
            if (containerRef.current) {
              // Find all video elements with the mapped RTC UIDs and set data-has-rtm
              const allRtcUids = [...new Set([
                ...Object.keys(updated).map(k => {
                  const num = parseInt(k)
                  return isNaN(num) ? null : num
                }).filter(Boolean),
                ...currentRemoteUsers.map(u => {
                  const num = typeof u.uid === 'string' ? parseInt(u.uid) : Number(u.uid)
                  return isNaN(num) ? null : num
                }).filter(Boolean),
              ])]
              
              allRtcUids.forEach((rtcUid) => {
                if (rtcUid === 0 || (agoraRtcProps?.uid !== undefined && rtcUid === agoraRtcProps.uid)) {
                  // Local user - always show
                  const elements = containerRef.current.querySelectorAll(`[data-uid="${rtcUid}"]`)
                  elements.forEach(el => el.setAttribute('data-has-rtm', 'true'))
                } else {
                  // Remote user - check if we have RTM data
                  const hasRtmData = updated[rtcUid] || updated[String(rtcUid)]
                  if (hasRtmData) {
                    const elements = containerRef.current.querySelectorAll(`[data-uid="${rtcUid}"]`)
                    elements.forEach(el => el.setAttribute('data-has-rtm', 'true'))
                  }
                }
              })
            }
          }, 100) // Small delay to ensure DOM is updated
          
          return updated
        } else {
          console.log('🟡 [REMOTE USER FLOW] ⏭️ SKIPPED updating uidToUsernameMap from rtm-user-joined', {
            uid,
            isRemoteUser: !isLocalUser,
            reason: !currentValue ? 'shouldUpdate is false (no current value)' : 
                    (isJson && !currentIsJson) ? 'shouldUpdate is false (JSON vs non-JSON)' :
                    (isJson && currentValue !== username) ? 'shouldUpdate is false (different JSON)' : 'unknown',
          })
        }
        return prev
      })
      
      // Check if this is a mentor
      try {
        const parsed = JSON.parse(username)
        if (parsed?.role === 'mentor' && !mentorUid) {
          setMentorUid(uidNum)
        }
      } catch (e) {
        // Not JSON, skip
      }
      
      agoraCallbacks?.['rtm-user-joined']?.(uid, username)
    },
    'rtm-user-left': (uid) => {
      const uidNum = parseInt(uid)
      const uidStr = uid.toString()
      
      setUidToUsernameMap((prev) => {
        const updated = { ...prev }
        // Delete all possible key formats
        delete updated[uid]
        delete updated[uidNum]
        delete updated[uidStr]
        return updated
      })
      
      if (mentorUid === uidNum) {
        setMentorUid(null)
      }
      
      agoraCallbacks?.['rtm-user-left']?.(uid)
    },
  }

  // Function to style status indicators like mentor portal
  const styleStatusIndicators = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    // Find all video cards - both participant and local user
    const allVideoCards = container.querySelectorAll('[data-uid], [data-role]')
    
    allVideoCards.forEach((videoCard) => {
      const uid = videoCard.getAttribute('data-uid')
      const role = videoCard.getAttribute('data-role')
      
      // Check if this is a small card (participant or local user, not mentor)
      const isSmallCard = role === 'participant' || (uid && (uid === '0' || parseInt(uid) === agoraRtcProps?.uid))
      const isMentorCard = role === 'mentor'
      
      if (!isSmallCard && !isMentorCard) return
      
      // Find status indicator containers - look for divs with SVG icons
      const statusContainers = videoCard.querySelectorAll('div[style*="position: absolute"]')
      
      statusContainers.forEach((container) => {
        const hasSvg = container.querySelector('svg[viewBox*="24"]')
        if (!hasSvg) return
        
        // Check if this is a status indicator (has mic/camera icons)
        const svgs = container.querySelectorAll('svg')
        const isStatusIndicator = Array.from(svgs).some(svg => {
          const viewBox = svg.getAttribute('viewBox') || ''
          return viewBox.includes('24')
        })
        
        if (!isStatusIndicator) return
        
        // Apply mentor portal styling
        container.style.background = 'rgba(0, 0, 0, 0.3)'
        container.style.backdropFilter = 'blur(8px) saturate(150%)'
        container.style.webkitBackdropFilter = 'blur(8px) saturate(150%)'
        container.style.borderRadius = '8px'
        container.style.padding = '4px 6px'
        container.style.border = '1px solid rgba(255, 255, 255, 0.2)'
        container.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'
        container.style.display = 'flex'
        container.style.gap = '4px'
        container.style.alignItems = 'center'
        
        // Position based on card type
        if (isSmallCard) {
          // Bottom left for small cards
          container.style.bottom = '8px'
          container.style.left = '8px'
          container.style.top = 'auto'
          container.style.right = 'auto'
          container.style.transform = 'none'
        } else if (isMentorCard) {
          // Top center for mentor cards
          container.style.left = '50%'
          container.style.transform = 'translateX(-50%)'
          container.style.right = 'auto'
          container.style.top = '8px'
          container.style.bottom = 'auto'
        }
        
        // Style individual SVG icons
        const icons = container.querySelectorAll('svg')
        icons.forEach((icon) => {
          icon.style.width = '16px'
          icon.style.height = '16px'
          icon.style.color = '#ffffff'
          icon.style.filter = 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
        })
      })
    })
  }, [agoraRtcProps?.uid])

  // Function to parse and update usernames from JSON - can be called independently
  const parseAndUpdateUsernames = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    // Get local user's username from RTM props (for chamber room, this is the JSON)
    const localUserUsername = agoraRtmProps?.username
    const localUserUid = agoraRtcProps?.uid

    // FIRST: Simple and aggressive - find ALL text nodes containing JSON and replace them
    // This catches cases where Agora UIKit renders JSON directly in text nodes
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    )
    
    let textNode
    while ((textNode = walker.nextNode())) {
      const text = textNode.textContent || ''
      
      // Check if this text node contains JSON
      if (text.includes('{') && text.includes('"name"') && text.length < 500) {
        // Check if this text node is within a video container
        let parent = textNode.parentElement
        let videoParent = null
        let uid = null
        
        while (parent && parent !== container) {
          const dataUid = parent.getAttribute('data-uid')
          if (dataUid) {
            videoParent = parent
            uid = dataUid
            break
          }
          // Also check if parent has video element (might be local user)
          if (parent.querySelector('video') || parent.closest('[data-uid]')) {
            const closestUid = parent.closest('[data-uid]')?.getAttribute('data-uid')
            if (closestUid) {
              videoParent = parent.closest('[data-uid]')
              uid = closestUid
              break
            }
            // For local user, might not have data-uid yet
            if (localUserUid !== undefined && localUserUsername) {
              videoParent = parent
              uid = localUserUid.toString()
              break
            }
          }
          parent = parent.parentElement
        }
        
        // If we found a video parent, try to parse and replace
        if (videoParent || uid) {
          try {
            const parsed = JSON.parse(text.trim())
            if (parsed && parsed.name && typeof parsed.name === 'string' && parsed.name !== 'Unknown') {
              // Replace the text node content
              textNode.textContent = parsed.name
              
              // Update the map
              if (uid) {
                const uidNum = parseInt(uid)
                if (!isNaN(uidNum)) {
                  setUidToUsernameMap((prev) => {
                    const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                    const isJson = text.includes('{') || text.includes('"name"')
                    const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    const shouldUpdate = !currentValue || (isJson && !currentIsJson) || (isJson && currentValue !== text.trim())
                    
                    if (shouldUpdate) {
                      return {
                        ...prev,
                        [uid]: text.trim(),
                        [uidNum]: text.trim(),
                        [uid.toString()]: text.trim(),
                      }
                    }
                    return prev
                  })
                }
              }
            }
          } catch (e) {
            // Try regex extraction as fallback
            const nameMatch = text.match(/"name"\s*:\s*"([^"]+)"/)
            if (nameMatch && nameMatch[1] && nameMatch[1] !== 'Unknown') {
              textNode.textContent = nameMatch[1]
              
              // Update the map
              if (uid) {
                const uidNum = parseInt(uid)
                if (!isNaN(uidNum)) {
                  setUidToUsernameMap((prev) => {
                    const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                    const isJson = text.includes('{') || text.includes('"name"')
                    const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    const shouldUpdate = !currentValue || (isJson && !currentIsJson) || (isJson && currentValue !== text.trim())
                    
                    if (shouldUpdate) {
                      return {
                        ...prev,
                        [uid]: text.trim(),
                        [uidNum]: text.trim(),
                        [uid.toString()]: text.trim(),
                      }
                    }
                    return prev
                  })
                }
              }
            }
          }
        }
      }
    }

    // SECOND: Do a comprehensive search for ALL elements that might contain JSON
    // This catches elements even if they don't match our specific selectors
    const allElements = container.querySelectorAll('*')
    
    allElements.forEach((el) => {
      const text = el.textContent || el.innerText || ''
      
      // Check if text looks like JSON (contains { and "name")
      if (text.includes('{') && text.includes('"name"') && text.length < 500) {
        // Skip if element is hidden
        const style = window.getComputedStyle(el)
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
          return
        }
        
        // Skip if element is inside a placeholder component
        const isInPlaceholder = el.closest('[class*="placeholder"], [class*="Placeholder"], [class*="CustomVideo"]')
        if (isInPlaceholder) {
          return
        }
        
        // Check if element is within a video container
        // For local user, the video might not have data-uid, so check if it's the local video
        let videoParent = el.closest('[data-uid]')
        let isLocalUserVideo = false
        
        // If no data-uid found, check if this might be the local user's video
        if (!videoParent && localUserUid !== undefined && localUserUsername) {
          // Check if element is in a container that might be the local video
          const possibleLocalContainer = el.closest('[class*="local"], [class*="Local"], [class*="video"]')
          if (possibleLocalContainer) {
            // Check if this container has a video element and no data-uid
            const hasVideo = possibleLocalContainer.querySelector('video')
            const hasDataUid = possibleLocalContainer.hasAttribute('data-uid') || possibleLocalContainer.closest('[data-uid]')
            if (hasVideo && !hasDataUid) {
              isLocalUserVideo = true
              videoParent = possibleLocalContainer
            }
          }
        }
        
        // Must be in a video container (with data-uid or identified as local)
        if (!videoParent && !isLocalUserVideo) {
          return
        }
        
        // Verify the video parent is actually visible
        if (videoParent) {
          const videoRect = videoParent.getBoundingClientRect()
          if (videoRect.width === 0 || videoRect.height === 0) {
            return
          }
        }
        
        // Be more aggressive - if it's in a video container and contains JSON, it's likely a username pill
        // Don't filter based on styling - Agora UIKit might render it in various ways
        const hasUsernameClass = el.classList.toString().toLowerCase().includes('username')
        const isTextElement = el.tagName === 'SPAN' || el.tagName === 'P' || el.tagName === 'DIV' || el.tagName === 'H1' || el.tagName === 'H2' || el.tagName === 'H3'
        
        // Only skip if it's clearly not a text element and doesn't have username class
        // This is more permissive to catch all username pills
        if (!hasUsernameClass && !isTextElement && el.tagName !== 'BUTTON' && el.tagName !== 'A') {
          // Still allow it if it has visible text content (might be a custom element)
          if (!text.trim() || text.trim().length < 3) {
            return
          }
        }
        
        // Determine which username to use
        // For local user video, use the RTM props username if available
        let usernameToParse = text
        if (isLocalUserVideo && localUserUsername) {
          // If text matches the local username or contains the same JSON structure, use local username
          if (text === localUserUsername || (text.includes('{') && localUserUsername.includes('{'))) {
            usernameToParse = localUserUsername
          }
        }
        
        try {
          const parsed = JSON.parse(usernameToParse)
          if (parsed && parsed.name && typeof parsed.name === 'string') {
            const role = parsed.role
            const shouldHide = role === 'auditor' || role === 'panelist'
            
            // Get UID from video parent
            let uid = null
            if (videoParent) {
              uid = videoParent.getAttribute('data-uid')
            } else if (isLocalUserVideo && localUserUid !== undefined) {
              uid = localUserUid.toString()
            }
            
            if (shouldHide) {
              el.textContent = ''
              el.innerText = ''
              el.style.display = 'none'
            } else {
              // Show name for any role (including chamber-room)
              // ALWAYS replace JSON with parsed name, regardless of whether it's in the map
              // This ensures videos always show names, not JSON
              if (text.includes('{') || text.includes('"name"') || isLocalUserVideo) {
                el.textContent = parsed.name
                el.innerText = parsed.name
                el.style.display = ''
                
                // ALSO update the uidToUsernameMap so focus dropdown can use it
                if (uid) {
                  const uidNum = parseInt(uid)
                  if (!isNaN(uidNum)) {
                    const isRemoteUser = !isLocalUserVideo && agoraRtcProps?.uid !== undefined && uidNum !== parseInt(agoraRtcProps.uid)
                    
                    console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames - Found JSON in DOM for UID:', {
                      uid,
                      uidNum,
                      isRemoteUser,
                      isLocalUserVideo,
                      usernameToParse: usernameToParse.length < 100 ? usernameToParse : usernameToParse.substring(0, 100) + '...',
                      parsedName: parsed.name,
                      role: parsed.role,
                    })
                    
                    setUidToUsernameMap((prev) => {
                      // ALWAYS update if we found JSON, even if a plain string exists
                      // This ensures we have complete RTM data
                      const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                      const isJson = usernameToParse.includes('{') || usernameToParse.includes('"name"')
                      const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                      
                      // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                      const shouldUpdate = !currentValue || 
                        (isJson && !currentIsJson) ||
                        (isJson && currentValue !== usernameToParse)
                      
                      console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames - Map update decision:', {
                        uid,
                        isRemoteUser,
                        currentValue: currentValue ? (currentValue.length < 100 ? currentValue : currentValue.substring(0, 100) + '...') : 'null',
                        currentIsJson,
                        isJson,
                        shouldUpdate,
                        prevMapKeys: Object.keys(prev),
                        prevMapSize: Object.keys(prev).length,
                      })
                      
                      if (shouldUpdate) {
                        const updated = {
                          ...prev,
                          [uid]: usernameToParse, // Store original JSON
                          [uidNum]: usernameToParse,
                          [uid.toString()]: usernameToParse,
                        }
                        console.log('🟢 [REMOTE USER FLOW] ✅ UPDATED uidToUsernameMap from parseAndUpdateUsernames', {
                          uid,
                          isRemoteUser,
                          newKeys: Object.keys(updated),
                          newMapSize: Object.keys(updated).length,
                          storedValue: usernameToParse.length < 100 ? usernameToParse : usernameToParse.substring(0, 100) + '...',
                        })
                        return updated
                      } else {
                        console.log('🟡 [REMOTE USER FLOW] ⏭️ SKIPPED updating uidToUsernameMap from parseAndUpdateUsernames', {
                          uid,
                          isRemoteUser,
                          reason: !currentValue ? 'shouldUpdate is false (no current value)' : 
                                  (isJson && !currentIsJson) ? 'shouldUpdate is false (JSON vs non-JSON)' :
                                  (isJson && currentValue !== usernameToParse) ? 'shouldUpdate is false (different JSON)' : 'unknown',
                        })
                      }
                      return prev
                    })
                  }
                }
              }
            }
          }
        } catch (e) {
          // Try regex extraction as fallback
          const nameMatch = text.match(/"name"\s*:\s*"([^"]+)"/)
          if (nameMatch && nameMatch[1]) {
            if (text.includes('{') || isLocalUserVideo) {
              el.textContent = nameMatch[1]
              el.innerText = nameMatch[1]
              el.style.display = ''
              
              // Update map for regex-extracted names too
              let uid = null
              if (videoParent) {
                uid = videoParent.getAttribute('data-uid')
              } else if (isLocalUserVideo && localUserUid !== undefined) {
                uid = localUserUid.toString()
              }
              
              if (uid) {
                const uidNum = parseInt(uid)
                if (!isNaN(uidNum)) {
                  setUidToUsernameMap((prev) => {
                    // ALWAYS update if we found JSON, even if a plain string exists
                    const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                    const isJson = text.includes('{') || text.includes('"name"')
                    const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                    
                    // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                    const shouldUpdate = !currentValue || 
                      (isJson && !currentIsJson) ||
                      (isJson && currentValue !== text)
                    
                    if (shouldUpdate) {
                      console.log('parseAndUpdateUsernames (regex) - Updating map for UID', uid, 'with text:', text, 'previous:', currentValue)
                      return {
                        ...prev,
                        [uid]: text, // Store original text (which contains JSON)
                        [uidNum]: text,
                        [uid.toString()]: text,
                      }
                    }
                    return prev
                  })
                }
              }
            }
          } else if (isLocalUserVideo && localUserUsername) {
            // Last resort: try parsing the local username directly
            try {
              const localParsed = JSON.parse(localUserUsername)
              if (localParsed?.name) {
                el.textContent = localParsed.name
                el.innerText = localParsed.name
                el.style.display = ''
              }
            } catch (e2) {
              // Failed, skip
            }
          }
        }
      }
    })
    
    // Also do targeted search within video containers (more efficient for known structures)
    const videoContainers = container.querySelectorAll('[data-uid]')
    
    videoContainers.forEach((videoContainer) => {
      const uid = videoContainer.getAttribute('data-uid')
      if (!uid) return
      
      // Find username elements within this specific video container
      const usernameSelectors = [
        `[data-uid="${uid}"] [class*="username"]`,
        `[data-uid="${uid}"] span`,
        `[data-uid="${uid}"] p`,
        `[data-uid="${uid}"] div[style*="background"]`,
      ]
      
      usernameSelectors.forEach((selector) => {
        try {
          const usernameElements = container.querySelectorAll(selector)
          
          usernameElements.forEach((el) => {
            const text = el.textContent || el.innerText || ''
            
            // Check if text looks like JSON
            if (text.includes('{') && text.includes('"name"')) {
              try {
                const parsed = JSON.parse(text)
                if (parsed && parsed.name && typeof parsed.name === 'string') {
                  const role = parsed.role
                  const shouldHide = role === 'auditor' || role === 'panelist'
                  
                  if (shouldHide) {
                    el.textContent = ''
                    el.innerText = ''
                    el.style.display = 'none'
                  } else {
                    // Always update if it's JSON
                    if (text.includes('{')) {
                      el.textContent = parsed.name
                      el.innerText = parsed.name
                      el.style.display = ''
                      
                      // ALSO update the uidToUsernameMap
                      const uidNum = parseInt(uid)
                      if (!isNaN(uidNum)) {
                        const isRemoteUser = agoraRtcProps?.uid !== undefined && uidNum !== parseInt(agoraRtcProps.uid)
                        
                        console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames (targeted) - Found JSON in DOM for UID:', {
                          uid,
                          uidNum,
                          isRemoteUser,
                          text: text.length < 100 ? text : text.substring(0, 100) + '...',
                          parsedName: parsed.name,
                          role: parsed.role,
                        })
                        
                        setUidToUsernameMap((prev) => {
                          // ALWAYS update if we found JSON, even if a plain string exists
                          const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                          const isJson = text.includes('{') || text.includes('"name"')
                          const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                          
                          // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                          const shouldUpdate = !currentValue || 
                            (isJson && !currentIsJson) ||
                            (isJson && currentValue !== text)
                          
                          console.log('🔵 [REMOTE USER FLOW] parseAndUpdateUsernames (targeted) - Map update decision:', {
                            uid,
                            isRemoteUser,
                            currentValue: currentValue ? (currentValue.length < 100 ? currentValue : currentValue.substring(0, 100) + '...') : 'null',
                            currentIsJson,
                            isJson,
                            shouldUpdate,
                            prevMapKeys: Object.keys(prev),
                            prevMapSize: Object.keys(prev).length,
                          })
                          
                          if (shouldUpdate) {
                            const updated = {
                              ...prev,
                              [uid]: text, // Store original JSON
                              [uidNum]: text,
                              [uid.toString()]: text,
                            }
                            console.log('🟢 [REMOTE USER FLOW] ✅ UPDATED uidToUsernameMap from parseAndUpdateUsernames (targeted)', {
                              uid,
                              isRemoteUser,
                              newKeys: Object.keys(updated),
                              newMapSize: Object.keys(updated).length,
                              storedValue: text.length < 100 ? text : text.substring(0, 100) + '...',
                            })
                            return updated
                          } else {
                            console.log('🟡 [REMOTE USER FLOW] ⏭️ SKIPPED updating uidToUsernameMap from parseAndUpdateUsernames (targeted)', {
                              uid,
                              isRemoteUser,
                              reason: !currentValue ? 'shouldUpdate is false (no current value)' : 
                                      (isJson && !currentIsJson) ? 'shouldUpdate is false (JSON vs non-JSON)' :
                                      (isJson && currentValue !== text) ? 'shouldUpdate is false (different JSON)' : 'unknown',
                            })
                          }
                          return prev
                        })
                      }
                    }
                  }
                }
              } catch (e) {
                // Try regex extraction
                const nameMatch = text.match(/"name"\s*:\s*"([^"]+)"/)
                if (nameMatch && nameMatch[1]) {
                  if (text.includes('{')) {
                    el.textContent = nameMatch[1]
                    el.innerText = nameMatch[1]
                    el.style.display = ''
                    
                    // Update map for regex-extracted names
                    const uidNum = parseInt(uid)
                    if (!isNaN(uidNum)) {
                      setUidToUsernameMap((prev) => {
                        // ALWAYS update if we found JSON, even if a plain string exists
                        const currentValue = prev[uid] || prev[uidNum] || prev[uid.toString()]
                        const isJson = text.includes('{') || text.includes('"name"')
                        const currentIsJson = currentValue && (currentValue.includes('{') || currentValue.includes('"name"'))
                        
                        // Update if: not set, or we found JSON and current is not JSON, or JSON is different
                        const shouldUpdate = !currentValue || 
                          (isJson && !currentIsJson) ||
                          (isJson && currentValue !== text)
                        
                        if (shouldUpdate) {
                          console.log('parseAndUpdateUsernames (targeted regex) - Updating map for UID', uid, 'with text:', text, 'previous:', currentValue)
                          return {
                            ...prev,
                            [uid]: text, // Store original text (which contains JSON)
                            [uidNum]: text,
                            [uid.toString()]: text,
                          }
                        }
                        return prev
                      })
                    }
                  }
                }
              }
            }
          })
        } catch (selectorError) {
          // Selector failed, continue
        }
      })
    })
    
    // Also extract already-parsed names (plain strings) from DOM and update map
    // This catches cases where the name is already displayed correctly on video card
    const allVideoContainers = container.querySelectorAll('[data-uid]')
    allVideoContainers.forEach((videoContainer) => {
      const uid = videoContainer.getAttribute('data-uid')
      if (!uid) return
      
      const uidNum = parseInt(uid)
      if (isNaN(uidNum)) return
      
      // Skip if we already have this UID in the map
      const currentMap = uidToUsernameMapRef.current
      if (currentMap[uid] || currentMap[uidNum] || currentMap[uid.toString()]) {
        return
      }
      
      // Skip local user
      if (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid)) {
        return
      }
      
      // Look for username elements that already have parsed names (not JSON)
      const usernameSelectors = [
        '[class*="username"]',
        'span[style*="background"]',
        'p[style*="background"]',
        'div[style*="background"][style*="position"]',
      ]
      
      for (const selector of usernameSelectors) {
        const usernameEls = videoContainer.querySelectorAll(selector)
        for (const el of usernameEls) {
          const text = (el.textContent || el.innerText || '').trim()
          // If it's a valid name (not JSON, not "User {uid}", reasonable length)
          if (text && 
              !text.includes('{') && 
              !text.includes('"') &&
              !text.match(/^User \d+$/) &&
              text.length > 2 &&
              text.length < 50 &&
              text !== 'Loading...' &&
              text !== 'Unknown' &&
              text !== 'Screening Room') {
            // Found a parsed name! Store it as a plain string
            console.log('parseAndUpdateUsernames - Found already-parsed name in DOM for UID', uid, ':', text)
            setUidToUsernameMap((prev) => {
              if (!prev[uid] && !prev[uidNum] && !prev[uid.toString()]) {
                return {
                  ...prev,
                  [uid]: text, // Store as plain string
                  [uidNum]: text,
                  [uid.toString()]: text,
                }
              }
              return prev
            })
            return // Found name, move to next container
          }
        }
      }
    })
  }, [agoraRtmProps?.username, agoraRtcProps?.uid])

  // Apply custom styling to video cards after render
  useEffect(() => {
    if (!containerRef.current) return

    const applyCustomStyling = () => {
      const container = containerRef.current
      if (!container) return
      
      // First, run the aggressive JSON parser
      parseAndUpdateUsernames()

      // Find all video elements - use multiple selectors to catch all cases
      const videoSelectors = [
        '[class*="video"]',
        '[data-uid]',
        'div[style*="position"]',
        '[class*="Video"]',
      ]
      
      const allVideoElements = new Set()
      videoSelectors.forEach((selector) => {
        try {
          const elements = container.querySelectorAll(selector)
          elements.forEach((el) => allVideoElements.add(el))
        } catch (e) {
          // Selector failed, continue
        }
      })
      
      allVideoElements.forEach((videoEl) => {
        // Try to get uid from data-uid attribute first
        let uid = videoEl.getAttribute('data-uid')
        
        // If no data-uid, try to find it in child elements or parent
        if (!uid) {
          const uidElement = videoEl.querySelector('[data-uid]') || videoEl.closest('[data-uid]')
          if (uidElement) {
            uid = uidElement.getAttribute('data-uid')
          }
        }
        
        // If still no uid and this might be local video, check if it matches local uid
        if (!uid && agoraRtcProps?.uid !== undefined) {
          // Check if this element contains the local video by checking for specific patterns
          const isLocalVideo = videoEl.querySelector('video') && 
            !videoEl.closest('[data-uid]') && 
            videoEl === container.querySelector('[class*="local"], [class*="Local"]')
          if (isLocalVideo) {
            uid = agoraRtcProps.uid.toString()
          }
        }
        
        if (!uid) return

        // Get username - check map first, then fallback to RTM props for local user
        const uidNum = parseInt(uid)
        let username = uidToUsernameMap[uid] || uidToUsernameMap[uidNum] || uidToUsernameMap[uid.toString()]
        if (!username && parseInt(uid) === agoraRtcProps?.uid && agoraRtmProps?.username) {
          username = agoraRtmProps.username
          // Debug: log when we use RTM props for local user
          console.log('ModernChamberVideoInterface - Using RTM props for local user:', {
            uid,
            username,
            agoraRtcPropsUid: agoraRtcProps?.uid,
          })
        }
        
        // Set data-has-rtm attribute based on whether we have RTM data
        // Local user (uid 0) is always shown, remote users need RTM data
        if (uidNum === 0 || (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid))) {
          // Local user - always show
          videoEl.setAttribute('data-has-rtm', 'true')
        } else if (username) {
          // Remote user with RTM data - show it
          videoEl.setAttribute('data-has-rtm', 'true')
        } else {
          // Remote user without RTM data yet - keep RTC video visible while identity catches up
          videoEl.setAttribute('data-has-rtm', 'false')
        }
        
        // Debug: log username mapping
        if (username && parseInt(uid) === agoraRtcProps?.uid) {
          console.log('ModernChamberVideoInterface - Local user username:', {
            uid,
            username,
            isInMap: !!uidToUsernameMap[uid],
            fromRtmProps: !uidToUsernameMap[uid] && !!agoraRtmProps?.username,
          })
        }
        
        let isMentor = false
        
        if (username) {
          try {
            const parsed = JSON.parse(username)
            isMentor = parsed?.role === 'mentor'
          } catch (e) {
            // Not JSON, skip
          }
        }

        // Set role attribute for CSS targeting
        if (isMentor || parseInt(uid) === mentorUid) {
          videoEl.setAttribute('data-role', 'mentor')
        } else {
          videoEl.setAttribute('data-role', 'participant')
        }
        
        // Update data-has-rtm attribute (already set above, but ensure it's correct)
        // This ensures videos are shown/hidden based on RTM data availability
        const currentHasRtm = videoEl.getAttribute('data-has-rtm')
        const shouldHaveRtm = uidNum === 0 || (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid)) || !!username
        if (shouldHaveRtm && currentHasRtm !== 'true') {
          videoEl.setAttribute('data-has-rtm', 'true')
        } else if (!shouldHaveRtm && currentHasRtm !== 'false') {
          videoEl.setAttribute('data-has-rtm', 'false')
        }

        // Update username display - search within this video element and globally
        // Use comprehensive approach to find ALL text elements that might contain username
        const searchContainers = [videoEl, container]
        
        searchContainers.forEach((searchContainer) => {
          // First, try to find elements by common selectors
          const usernameSelectors = [
            `[data-uid="${uid}"] [class*="username"]`,
            `[data-uid="${uid}"] span`,
            `[data-uid="${uid}"] p`,
            `[data-uid="${uid}"] div[style*="background"]`,
            `[data-uid="${uid}"] div[style*="position"]`,
            `[class*="username"]`,
            `span`,
            `p`,
            `div[style*="background"]`,
          ]
          
          usernameSelectors.forEach((selector) => {
            try {
              const usernameEls = searchContainer.querySelectorAll(selector)
              usernameEls.forEach((usernameEl) => {
                if (!usernameEl) return
                
                const currentText = usernameEl.textContent || usernameEl.innerText || ''
                
                // Skip if this element is not related to this uid (check parent chain)
                const parentWithUid = usernameEl.closest('[data-uid]')
                if (parentWithUid && parentWithUid.getAttribute('data-uid') !== uid) {
                  return
                }
                
                // Check if current text looks like JSON (contains { and "name")
                const looksLikeJson = currentText.includes('{') && currentText.includes('"name"')
                
                // If we have a username, try to parse and update
                if (username) {
                  try {
                    const parsed = JSON.parse(username)
                    const role = parsed?.role
                    
                    // Hide auditor and panelist
                    if (role === 'auditor' || role === 'panelist') {
                      usernameEl.style.display = 'none'
                      return
                    }
                    
                    // Show name for any role (including chamber-room)
                    if (parsed?.name && parsed.name !== 'Unknown') {
                      // Always update if current text is JSON or different
                      if (looksLikeJson || currentText === username || currentText !== parsed.name) {
                        usernameEl.textContent = parsed.name
                        usernameEl.innerText = parsed.name
                        usernameEl.style.display = ''
                      }
                    } else if (parsed?.name === 'Unknown') {
                      usernameEl.style.display = 'none'
                    }
                  } catch (e) {
                    // If username is not JSON, but current text is JSON, try to extract from current text
                    if (looksLikeJson) {
                      try {
                        const parsedCurrent = JSON.parse(currentText)
                        if (parsedCurrent?.name && parsedCurrent.name !== 'Unknown') {
                          usernameEl.textContent = parsedCurrent.name
                          usernameEl.innerText = parsedCurrent.name
                          usernameEl.style.display = ''
                        }
                      } catch (e2) {
                        // Try regex extraction
                        const nameMatch = currentText.match(/"name"\s*:\s*"([^"]+)"/)
                        if (nameMatch && nameMatch[1]) {
                          usernameEl.textContent = nameMatch[1]
                          usernameEl.innerText = nameMatch[1]
                          usernameEl.style.display = ''
                        }
                      }
                    } else if (username && !username.includes('{') && !username.includes('"')) {
                      // Plain string username
                      if (currentText !== username) {
                        usernameEl.textContent = username
                        usernameEl.innerText = username
                        usernameEl.style.display = ''
                      }
                    }
                  }
                } else if (looksLikeJson) {
                  // No username in map, but current text is JSON - try to parse it
                  try {
                    const parsedCurrent = JSON.parse(currentText)
                    if (parsedCurrent?.name && parsedCurrent.name !== 'Unknown') {
                      usernameEl.textContent = parsedCurrent.name
                      usernameEl.innerText = parsedCurrent.name
                      usernameEl.style.display = ''
                    }
                  } catch (e2) {
                    // Try regex extraction
                    const nameMatch = currentText.match(/"name"\s*:\s*"([^"]+)"/)
                    if (nameMatch && nameMatch[1]) {
                      usernameEl.textContent = nameMatch[1]
                      usernameEl.innerText = nameMatch[1]
                      usernameEl.style.display = ''
                    }
                  }
                }
              })
            } catch (selectorError) {
              // Selector failed, continue
            }
          })
          
          // Also search for any text node that contains the JSON string directly
          // This is a fallback for cases where the JSON is in a text node
          if (username && username.includes('{')) {
            try {
              const parsed = JSON.parse(username)
              if (parsed?.name && parsed.name !== 'Unknown') {
                // Walk through all text nodes in the container
                const walker = document.createTreeWalker(
                  searchContainer,
                  NodeFilter.SHOW_TEXT,
                  null,
                  false
                )
                
                let node
                while ((node = walker.nextNode())) {
                  const text = node.textContent || ''
                  // Check if this text node contains the JSON
                  if (text.includes(username) || (text.includes('{') && text.includes('"name"') && text.includes(parsed.name))) {
                    // Find the parent element
                    let parent = node.parentElement
                    while (parent && parent !== searchContainer) {
                      // Check if this parent is related to our uid
                      const parentUid = parent.getAttribute('data-uid') || 
                        parent.closest('[data-uid]')?.getAttribute('data-uid')
                      if (parentUid === uid || (!parentUid && parseInt(uid) === agoraRtcProps?.uid)) {
                        // Update the text node
                        node.textContent = parsed.name
                        break
                      }
                      parent = parent.parentElement
                    }
                  }
                }
              }
            } catch (e) {
              // Failed to parse, skip
            }
          }
        })
      })
    }

    // Also update data-has-rtm attributes for all video elements
    const updateRtmAttributes = () => {
      if (!containerRef.current) return
      const allVideoElements = containerRef.current.querySelectorAll('[data-uid]')
      allVideoElements.forEach((videoEl) => {
        const uid = videoEl.getAttribute('data-uid')
        if (!uid) return
        
        const uidNum = parseInt(uid)
        const isLocalUser = uidNum === 0 || (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid))
        const hasRtmData = uidToUsernameMap[uid] || uidToUsernameMap[uidNum] || uidToUsernameMap[uid.toString()]
        
        if (isLocalUser) {
          videoEl.setAttribute('data-has-rtm', 'true')
        } else if (hasRtmData) {
          videoEl.setAttribute('data-has-rtm', 'true')
        } else {
          videoEl.setAttribute('data-has-rtm', 'false')
        }
      })
    }

    // Apply styling immediately and on interval
    applyCustomStyling()
    parseAndUpdateUsernames() // Run immediately
    styleStatusIndicators() // Style status indicators
    updateRtmAttributes() // Update RTM attributes immediately
    
    const interval = setInterval(() => {
      parseAndUpdateUsernames() // Run parser on interval too
      applyCustomStyling()
      styleStatusIndicators() // Style status indicators on interval
      updateRtmAttributes() // Update RTM attributes
    }, 100) // More frequent updates

    // Set up MutationObserver to watch for DOM changes (like mentor portal)
    const observer = new MutationObserver(() => {
      // When DOM changes, re-parse usernames immediately
      parseAndUpdateUsernames()
      // Also run styling after a small delay to ensure DOM is stable
      setTimeout(() => {
        applyCustomStyling()
        styleStatusIndicators()
        updateRtmAttributes() // Update RTM attributes when DOM changes
      }, 50)
    })

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true, // Watch for attribute changes too
        attributeFilter: ['data-uid', 'data-role'], // Only watch relevant attributes
      })
    }

    return () => {
      clearInterval(interval)
      observer.disconnect()
    }
  }, [uidToUsernameMap, agoraRtmProps?.username, agoraRtcProps?.uid, mentorUid, parseAndUpdateUsernames, styleStatusIndicators])
  
  // Also run parseAndUpdateUsernames when username map updates
  useEffect(() => {
    if (uidToUsernameMap && Object.keys(uidToUsernameMap).length > 0) {
      // Run immediately and after a delay
      parseAndUpdateUsernames()
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 50)
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 200)
    }
  }, [uidToUsernameMap, parseAndUpdateUsernames])

  // Periodically extract usernames from DOM and update map (fallback when rtm-user-joined doesn't fire)
  useEffect(() => {
    if (!containerRef.current) return

    const extractUsernamesFromDom = () => {
      try {
        // Find all video containers with data-uid
        const videoContainers = containerRef.current.querySelectorAll('[data-uid]')
        
        videoContainers.forEach((container) => {
          const uid = container.getAttribute('data-uid')
          if (!uid) return
          
          const uidNum = parseInt(uid)
          if (isNaN(uidNum)) return
          
          // Skip if we already have this UID in the map
          if (uidToUsernameMap[uid] || uidToUsernameMap[uidNum] || uidToUsernameMap[uid.toString()]) {
            return
          }
          
          // Skip local user
          if (agoraRtcProps?.uid !== undefined && uidNum === parseInt(agoraRtcProps.uid)) {
            return
          }
          
          // Try to find username text in this container
          const usernameSelectors = [
            '[class*="username"]',
            'span[style*="background"]',
            'p[style*="background"]',
            'div[style*="background"][style*="position"]',
          ]
          
          for (const selector of usernameSelectors) {
            const usernameEls = container.querySelectorAll(selector)
            for (const el of usernameEls) {
              const text = (el.textContent || el.innerText || '').trim()
              // If it's a valid name (not JSON, not "User {uid}", reasonable length)
              if (text && 
                  !text.includes('{') && 
                  !text.includes('"') &&
                  !text.match(/^User \d+$/) &&
                  text.length > 2 &&
                  text.length < 50 &&
                  text !== 'Loading...' &&
                  text !== 'Unknown' &&
                  text !== 'Screening Room') {
                // Found a name! Store it (as plain string since it's not JSON)
                console.log('Extracted username from DOM for UID', uid, ':', text)
                setUidToUsernameMap((prev) => ({
                  ...prev,
                  [uid]: text,
                  [uidNum]: text,
                  [uid.toString()]: text,
                }))
                return // Found name, move to next container
              }
            }
          }
        })
      } catch (e) {
        console.error('Error extracting usernames from DOM:', e)
      }
    }

    // Run immediately
    extractUsernamesFromDom()
    
    // Also run periodically (every 2 seconds) to catch names that appear later
    const interval = setInterval(extractUsernamesFromDom, 2000)
    
    return () => clearInterval(interval)
  }, [containerRef, uidToUsernameMap, agoraRtcProps?.uid])
  
  // Run when RTM props update (especially for local user)
  useEffect(() => {
    if (agoraRtmProps?.username) {
      // Run immediately and after delays to catch all cases
      parseAndUpdateUsernames()
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 50)
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 200)
      setTimeout(() => {
        parseAndUpdateUsernames()
      }, 500)
    }
  }, [agoraRtmProps?.username, parseAndUpdateUsernames])

  // Memoize the placeholder wrapper to ensure it updates when uidToUsernameMap changes
  // This ensures remote users' names are properly displayed when RTM data arrives
  const customVideoPlaceholderWrapper = useMemo(() => {
    return (props) => (
      <ChamberVideoPlaceholder {...props} uidToUsernameMap={uidToUsernameMap} chamberName={chamberName} />
    )
  }, [uidToUsernameMap, chamberName])

  return (
    <VideoContainer ref={containerRef} className={`chamber-video-container ${showControls ? '' : 'controls-hidden'}`}>
      <VideoCardStyles />
      

      {/* Hero Video Wrapper - Mentor gets prominent display */}
      <HeroVideoWrapper>
        <AgoraUIKit
          rtcProps={{
            ...agoraRtcProps,
            layout: layout.pin, // Use pin layout for better control
            CustomVideoPlaceholder: customVideoPlaceholderWrapper,
          }}
          callbacks={enhancedCallbacks}
          styleProps={{
            UIKitContainer: {
              backgroundColor: 'transparent',
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            },
            videoMode: {
              max: 'cover',
              min: 'cover',
            },
            usernameText: {
              background: 'rgba(103, 121, 227, 0.85)',
              backdropFilter: 'blur(16px) saturate(200%)',
              WebkitBackdropFilter: 'blur(16px) saturate(200%)',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: '5px',
              boxShadow: '0 3px 12px rgba(103, 121, 227, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
              letterSpacing: '0.1px',
              margin: '10px',
              maxWidth: 'calc(100% - 20px)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.3,
            },
            localBtnContainer: {
              background: 'rgba(15, 15, 15, 0.75)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1.5px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '12px 16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), 0 4px 16px rgba(139, 92, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              position: 'absolute',
              bottom: '24px',
              left: '50%',
              right: 'auto',
              transform: 'translateX(-50%)',
              WebkitTransform: 'translateX(-50%)',
              zIndex: 10000,
              pointerEvents: 'none', // Container remains pointer-events none
              width: 'fit-content', // Fit content width
              minWidth: 'auto', // Remove any min-width constraints
              maxWidth: 'fit-content',
            },
            BtnTemplateStyles: {
              width: '48px',
              height: '48px',
              minWidth: '48px',
              minHeight: '48px',
              maxWidth: '48px',
              maxHeight: '48px',
              flexShrink: 0,
              flexGrow: 0,
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(16px) saturate(180%)',
              WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              border: '1.5px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(139, 92, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: 'auto',
              zIndex: 10001,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                borderColor: 'rgba(139, 92, 246, 0.6)',
                transform: 'translateY(-2px) scale(1.05)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(139, 92, 246, 0.25), inset 0 1px 0 rgba(255, 255, 255, 1), 0 0 20px rgba(139, 92, 246, 0.3)',
              },
            },
            localBtnStyles: {
              muteLocalAudio: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1.5px solid rgba(103, 121, 227, 0.3)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(103, 121, 227, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)',
                color: '#1f2937',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  borderColor: 'rgba(103, 121, 227, 0.6)',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(103, 121, 227, 0.25), inset 0 1px 0 rgba(255, 255, 255, 1), 0 0 20px rgba(103, 121, 227, 0.3)',
                },
              },
              muteLocalVideo: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1.5px solid rgba(103, 121, 227, 0.3)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(103, 121, 227, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)',
                color: '#1f2937',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  borderColor: 'rgba(103, 121, 227, 0.6)',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(103, 121, 227, 0.25), inset 0 1px 0 rgba(255, 255, 255, 1), 0 0 20px rgba(103, 121, 227, 0.3)',
                },
              },
              screenshare: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1.5px solid rgba(56, 189, 248, 0.3)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(56, 189, 248, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)',
                color: '#1f2937',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  borderColor: 'rgba(56, 189, 248, 0.6)',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(56, 189, 248, 0.25), inset 0 1px 0 rgba(255, 255, 255, 1), 0 0 20px rgba(56, 189, 248, 0.3)',
                },
              },
              settings: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1.5px solid rgba(168, 85, 247, 0.3)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(168, 85, 247, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)',
                color: '#1f2937',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  borderColor: 'rgba(168, 85, 247, 0.6)',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(168, 85, 247, 0.25), inset 0 1px 0 rgba(255, 255, 255, 1), 0 0 20px rgba(168, 85, 247, 0.3)',
                },
              },
              endCall: {
                width: '52px',
                height: '52px',
                minWidth: '52px',
                minHeight: '52px',
                maxWidth: '52px',
                maxHeight: '52px',
                flexShrink: 0,
                flexGrow: 0,
                backgroundColor: 'rgba(239, 68, 68, 0.95)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1.5px solid rgba(239, 68, 68, 0.8)',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3), 0 4px 12px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 1)',
                  borderColor: 'rgba(239, 68, 68, 0.9)',
                  boxShadow: '0 12px 32px rgba(239, 68, 68, 0.4), 0 6px 16px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 0 20px rgba(239, 68, 68, 0.4)',
                },
              },
            },
            remoteBtnStyles: {
              muteRemoteAudio: {
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                boxShadow: '0 4px 16px rgba(239, 68, 68, 0.2)',
              },
              muteRemoteVideo: {
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                boxShadow: '0 4px 16px rgba(239, 68, 68, 0.2)',
              },
            },
            theme: '#475569',
            iconSize: 24,
          }}
          rtmProps={agoraRtmProps}
        />
      </HeroVideoWrapper>
      
      {/* Focus Selection Button */}
      <Box
        className="focus-selection-button"
        sx={{
          position: 'absolute',
          bottom: 100,
          left: 24,
          zIndex: 100,
        }}
      >
        <Tooltip title="Select video to focus" arrow>
          <IconButton
            onClick={(e) => setFocusMenuAnchor(e.currentTarget)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(16px) saturate(180%)',
              WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              border: '1.5px solid rgba(103, 121, 227, 0.3)',
              color: '#1f2937',
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(103, 121, 227, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                borderColor: 'rgba(103, 121, 227, 0.6)',
                transform: 'translateY(-2px) scale(1.05)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 6px 16px rgba(103, 121, 227, 0.25), inset 0 1px 0 rgba(255, 255, 255, 1), 0 0 20px rgba(103, 121, 227, 0.3)',
              },
            }}
          >
            <CenterFocusStrongIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Focus Selection Menu */}
      <Menu
        anchorEl={focusMenuAnchor}
        open={focusMenuOpen}
        onClose={() => setFocusMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxHeight: 400,
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(103, 121, 227, 0.1)',
            overflow: 'auto',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        {/* Include local user in focus menu if RTM props are available */}
        {agoraRtcProps?.uid !== undefined && agoraRtmProps?.username && (() => {
          const localParsed = parseRtmUsername(agoraRtmProps.username)
          const localDisplayName = localParsed?.name || 'Me'
          const localProfilePicUrl = getProfilePictureUrl(localParsed?.profilePicId)
          
          return (
            <MenuItem
              key={`local-${agoraRtcProps.uid}`}
              onClick={() => {
                try {
                  const videoContainer = containerRef.current
                  if (videoContainer) {
                    // Find local user's video element
                    const localVideoElement = videoContainer.querySelector(`[data-uid="${agoraRtcProps.uid}"]`) ||
                      videoContainer.querySelector('[class*="local"], [class*="Local"]')
                    if (localVideoElement) {
                      localVideoElement.click()
                    }
                  }
                } catch (error) {
                  console.error('Error focusing local user:', error)
                }
                setFocusMenuAnchor(null)
              }}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Avatar
                  src={localProfilePicUrl || undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    background: localProfilePicUrl
                      ? 'transparent'
                      : 'linear-gradient(135deg, #6779E3 0%, #7450AA 100%)',
                    fontSize: '14px',
                    fontWeight: 600,
                    border: '2px solid rgba(103, 121, 227, 0.2)',
                    objectFit: 'cover',
                  }}
                  imgProps={{
                    onError: (e) => {
                      console.warn('Focus menu local user profile picture failed to load:', localProfilePicUrl)
                      e.target.style.display = 'none'
                      e.target.src = ''
                    },
                    onLoad: () => {
                      console.log('Focus menu local user profile picture loaded:', localProfilePicUrl)
                    },
                    crossOrigin: 'anonymous',
                    loading: 'eager',
                  }}
                >
                  {!localProfilePicUrl && getInitials(localDisplayName)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={localDisplayName}
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#4a5568',
                }}
              />
            </MenuItem>
          )
        })()}
        {/* Get all users from uidToUsernameMap (includes both remote and local) */}
        {(() => {
          // Force re-render when map or remote users change
          const mapKeys = Object.keys(uidToUsernameMap)
          const remoteUserUids = remoteUsers.map(u => u.uid).join(',')
          
          // Debug logging
          console.log('Focus menu - Debug info:', {
            remoteUsersCount: remoteUsers.length,
            remoteUsers: remoteUsers.map(u => ({ uid: u.uid, uidType: typeof u.uid })),
            uidToUsernameMapKeys: mapKeys,
            uidToUsernameMapSize: mapKeys.length,
            uidToUsernameMap: uidToUsernameMap,
            localUid: agoraRtcProps?.uid,
            mapKeyTypes: mapKeys.map(k => ({ key: k, type: typeof k, parsed: parseInt(k) })),
          })
          
          // Create a set of all UIDs from both remoteUsers and uidToUsernameMap
          const allUserUids = new Set()
          
          // Add remote users first (these are users who have joined via RTC)
          remoteUsers.forEach((user) => {
            if (user?.uid !== undefined) {
              allUserUids.add(parseInt(user.uid))
            }
          })
          
          // Add all UIDs from uidToUsernameMap (includes local user and remote users from RTM)
          // Deduplicate by numeric UID to avoid showing same user multiple times
          // Also exclude local user here to prevent multiple entries
          const localUidNum = agoraRtcProps?.uid !== undefined ? parseInt(agoraRtcProps.uid) : null
          const seenNumericUids = new Set()
          Object.keys(uidToUsernameMap).forEach((uid) => {
            const uidNum = parseInt(uid)
            if (!isNaN(uidNum) && 
                !seenNumericUids.has(uidNum) && 
                (localUidNum === null || uidNum !== localUidNum)) { // Exclude local user
              seenNumericUids.add(uidNum)
              allUserUids.add(uidNum)
            }
          })
          
          console.log('Focus menu - All UIDs:', Array.from(allUserUids))
          
          // Convert to array and filter
          // Additional deduplication: ensure no local user entries
          // (localUidNum already declared above)
          const uniqueUids = Array.from(allUserUids).filter((uid) => {
            // Skip local user completely
            if (localUidNum !== null && parseInt(uid) === localUidNum) {
              return false
            }
            return true
          })
          
          return uniqueUids
            .filter((uid) => {
              // Double-check: Skip local user (already filtered above, but extra safety)
              if (localUidNum !== null && parseInt(uid) === localUidNum) {
                return false
              }
              
              // Try multiple ways to get username from map
              let rawUsername = uidToUsernameMap[uid] || uidToUsernameMap[uid.toString()]
              console.log('🔵 [REMOTE USER FLOW] Focus dropdown - Looking up UID in map:', {
                uid,
                directLookup: uidToUsernameMap[uid] ? 'found' : 'not found',
                stringLookup: uidToUsernameMap[uid.toString()] ? 'found' : 'not found',
                mapKeys: Object.keys(uidToUsernameMap),
                mapSize: Object.keys(uidToUsernameMap).length,
              })
              
              if (!rawUsername) {
                const matchingKey = Object.keys(uidToUsernameMap).find(
                  (key) => parseInt(key) === parseInt(uid)
                )
                if (matchingKey) {
                  rawUsername = uidToUsernameMap[matchingKey]
                  console.log('🔵 [REMOTE USER FLOW] Focus dropdown - Found via matching key:', {
                    uid,
                    matchingKey,
                    rawUsername: rawUsername ? (rawUsername.length < 100 ? rawUsername : rawUsername.substring(0, 100) + '...') : 'null',
                  })
                } else {
                  console.log('🔵 [REMOTE USER FLOW] Focus dropdown - No matching key found for UID:', {
                    uid,
                    searchedKeys: Object.keys(uidToUsernameMap),
                    allKeysAsNumbers: Object.keys(uidToUsernameMap).map(k => parseInt(k)),
                  })
                }
              } else {
                console.log('🔵 [REMOTE USER FLOW] Focus dropdown - Found via direct lookup:', {
                  uid,
                  rawUsername: rawUsername.length < 100 ? rawUsername : rawUsername.substring(0, 100) + '...',
                })
              }
              
              // If no username in map but user is in remoteUsers, still show them
              // (they might not have RTM data yet)
              if (!rawUsername) {
                const isInRemoteUsers = remoteUsers.some((u) => parseInt(u.uid) === parseInt(uid))
                console.log('🔵 [REMOTE USER FLOW] Focus dropdown - No username in map, checking remoteUsers:', {
                  uid,
                  isInRemoteUsers,
                  remoteUserUids: remoteUsers.map(u => u.uid),
                })
                if (isInRemoteUsers) {
                  // Show them with a default name
                  return true
                }
                return false
              }
              
              try {
                const parsed = parseRtmUsername(rawUsername)
                const role = parsed?.role
                // Show mentor, patient, and chamber-room (hide auditor and panelist)
                return role !== 'auditor' && role !== 'panelist'
              } catch {
                return true
              }
            })
            .map((uid) => {
              // Find the user object from remoteUsers if available, otherwise create a minimal one
              const user = remoteUsers.find((u) => parseInt(u.uid) === parseInt(uid)) || { uid }
              
              // Try multiple ways to get username from map - be very thorough
              let rawUsername = null
              
              // Try direct access
              rawUsername = uidToUsernameMap[uid] || uidToUsernameMap[uid.toString()]
              
              // If not found, try finding by numeric match
              if (!rawUsername) {
                const matchingKey = Object.keys(uidToUsernameMap).find(
                  (key) => parseInt(key) === parseInt(uid)
                )
                if (matchingKey) {
                  rawUsername = uidToUsernameMap[matchingKey]
                }
              }
              
              // If still not found, try all keys and check if any match when converted to number
              if (!rawUsername) {
                for (const key of Object.keys(uidToUsernameMap)) {
                  if (parseInt(key) === parseInt(uid)) {
                    rawUsername = uidToUsernameMap[key]
                    break
                  }
                }
              }
              
              // If still no username, try to get it from the DOM (video screen might have it)
              let displayNameFromDom = null
              if (!rawUsername && containerRef.current) {
                try {
                  // Strategy 1: Try to find video element by data-uid attribute
                  let videoElement = containerRef.current.querySelector(`[data-uid="${uid}"]`) ||
                    containerRef.current.querySelector(`div[data-uid="${uid}"]`) ||
                    containerRef.current.querySelector(`[data-uid="${uid.toString()}"]`)
                  
                  // Strategy 2: If not found, search for all video elements and check their parent/container
                  if (!videoElement) {
                    const allVideos = containerRef.current.querySelectorAll('video')
                    for (const video of allVideos) {
                      // Check if this video's container has the UID
                      const container = video.closest('[data-uid]') || video.parentElement?.closest('[data-uid]')
                      if (container) {
                        const containerUid = container.getAttribute('data-uid')
                        if (containerUid && (parseInt(containerUid) === parseInt(uid) || containerUid === uid.toString())) {
                          videoElement = container
                          break
                        }
                      }
                    }
                  }
                  
                  // Strategy 3: Search for any element containing the UID in its attributes or nearby
                  if (!videoElement) {
                    const allElements = containerRef.current.querySelectorAll('*')
                    for (const el of allElements) {
                      const dataUid = el.getAttribute('data-uid')
                      if (dataUid && (parseInt(dataUid) === parseInt(uid) || dataUid === uid.toString())) {
                        videoElement = el
                        break
                      }
                    }
                  }
                  
                  if (videoElement) {
                    // Look for username elements in the video card - try multiple selectors
                    const usernameSelectors = [
                      '[class*="username"]',
                      'span[style*="background"]',
                      'p[style*="background"]',
                      'div[style*="background"][style*="position"]',
                      'span',
                      'p',
                    ]
                    
                    for (const selector of usernameSelectors) {
                      const usernameEls = videoElement.querySelectorAll(selector)
                      for (const el of usernameEls) {
                        const text = (el.textContent || el.innerText || '').trim()
                        // If it's not JSON, not "User {uid}", and has reasonable length, use it
                        if (text && 
                            !text.includes('{') && 
                            !text.includes('"name"') && 
                            !text.includes('"role"') &&
                            text !== `User ${uid}` &&
                            text.length > 0 &&
                            text.length < 100 &&
                            text !== 'Loading...' &&
                            text !== 'Unknown') {
                          displayNameFromDom = text
                          console.log('Found name in DOM for UID', uid, ':', displayNameFromDom, 'from element:', el)
                          break
                        }
                      }
                      if (displayNameFromDom) break
                    }
                  } else {
                    // Strategy 4: Search entire container for any text that looks like a name
                    // This is a fallback when we can't find the video element
                    const allTextElements = containerRef.current.querySelectorAll('span, p, div')
                    for (const el of allTextElements) {
                      const text = (el.textContent || el.innerText || '').trim()
                      // Look for text that's not JSON, not a UID, and looks like a name
                      if (text && 
                          !text.includes('{') && 
                          !text.includes('"') &&
                          !text.match(/^\d+$/) && // Not just numbers
                          text !== `User ${uid}` &&
                          text.length > 2 &&
                          text.length < 50 &&
                          text !== 'Loading...' &&
                          text !== 'Unknown' &&
                          text !== 'Screening Room') { // Exclude local user's name
                        // Check if this element is near a video element (within reasonable distance)
                        const nearbyVideo = el.closest('[data-uid]') || 
                                          el.parentElement?.querySelector('video')?.closest('[data-uid]')
                        if (nearbyVideo) {
                          const nearbyUid = nearbyVideo.getAttribute('data-uid')
                          if (nearbyUid && (parseInt(nearbyUid) === parseInt(uid) || nearbyUid === uid.toString())) {
                            displayNameFromDom = text
                            console.log('Found name in DOM (fallback) for UID', uid, ':', displayNameFromDom)
                            break
                          }
                        }
                      }
                    }
                    if (!displayNameFromDom) {
                      console.log('Could not find video element or name in DOM for UID', uid)
                    }
                  }
                } catch (e) {
                  console.error('DOM query failed for UID', uid, ':', e)
                }
              }
              
              // Parse the username
              let parsed = { role: null, name: null, profilePicId: null }
              if (rawUsername) {
                // Check if it's JSON or plain string
                if (rawUsername.includes('{') || rawUsername.includes('"')) {
                  parsed = parseRtmUsername(rawUsername)
                } else {
                  // Plain string username (like "Ashna Garg")
                  parsed = { role: null, name: rawUsername, profilePicId: null }
                }
              }
              
              // Get display name - prefer parsed name, then DOM name, fallback to User {uid}
              let displayName = null
              if (parsed?.name && parsed.name !== 'Unknown' && parsed.name !== 'Loading...') {
                displayName = parsed.name
              } else if (displayNameFromDom) {
                displayName = displayNameFromDom
              } else {
                displayName = `User ${uid}`
              }
              
              const profilePicUrl = getProfilePictureUrl(parsed?.profilePicId)
              
              // Debug logging with more detail
              console.log('Focus menu - Participant details:', {
                uid,
                uidType: typeof uid,
                rawUsername,
                rawUsernameType: typeof rawUsername,
                parsed,
                profilePicId: parsed?.profilePicId,
                profilePicUrl,
                displayName,
                displayNameFromDom,
                mapKeys: Object.keys(uidToUsernameMap),
                mapValues: Object.values(uidToUsernameMap).map(v => {
                  if (typeof v === 'string' && v.length < 100) {
                    try {
                      const p = JSON.parse(v)
                      return `{role:${p.role},name:${p.name}}`
                    } catch {
                      return v
                    }
                  }
                  return '...'
                }),
                foundInMap: !!rawUsername,
                foundInDom: !!displayNameFromDom,
              })
              
              return (
                <MenuItem
                  key={uid}
                  onClick={() => {
                    try {
                      const videoContainer = containerRef.current
                      if (videoContainer) {
                        const videoElement = videoContainer.querySelector(`[data-uid="${uid}"]`) ||
                          videoContainer.querySelector(`div[style*="position"][data-uid="${uid}"]`)
                        if (videoElement) {
                          videoElement.click()
                        } else {
                          const allVideos = videoContainer.querySelectorAll('div[style*="position"]')
                          allVideos.forEach((videoDiv) => {
                            const uidAttr = videoDiv.getAttribute('data-uid')
                            if (uidAttr && parseInt(uidAttr) === parseInt(uid)) {
                              videoDiv.click()
                            }
                          })
                        }
                      }
                    } catch (error) {
                      console.error('Error focusing user:', error)
                    }
                    setFocusMenuAnchor(null)
                  }}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    src={profilePicUrl || undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      background: profilePicUrl
                        ? 'transparent'
                        : 'linear-gradient(135deg, #6779E3 0%, #7450AA 100%)',
                      fontSize: '14px',
                      fontWeight: 600,
                      border: '2px solid rgba(103, 121, 227, 0.2)',
                      objectFit: 'cover',
                    }}
                    imgProps={{
                      onError: (e) => {
                        console.warn('Focus menu profile picture failed to load:', profilePicUrl, 'for user:', displayName)
                        e.target.style.display = 'none'
                        e.target.src = ''
                      },
                      onLoad: () => {
                        console.log('Focus menu profile picture loaded:', profilePicUrl, 'for user:', displayName)
                      },
                      crossOrigin: 'anonymous',
                      loading: 'eager',
                    }}
                  >
                    {!profilePicUrl && getInitials(displayName)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={displayName}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#4a5568',
                  }}
                />
              </MenuItem>
            )
          })
        })()}
        {/* Show "No participants" only if there are no users at all (including local) */}
        {(() => {
          const hasLocalUser = agoraRtcProps?.uid !== undefined && agoraRtmProps?.username
          const hasRemoteUsers = Array.from(new Set([
            ...remoteUsers.map(u => u.uid),
            ...Object.keys(uidToUsernameMap).map(k => parseInt(k)).filter(k => !isNaN(k))
          ])).filter(uid => {
            if (agoraRtcProps?.uid !== undefined && parseInt(uid) === parseInt(agoraRtcProps.uid)) {
              return false // Exclude local user from count
            }
            const rawUsername = uidToUsernameMap[uid] || uidToUsernameMap[uid.toString()]
            if (!rawUsername) return false
            try {
              const parsed = parseRtmUsername(rawUsername)
              return parsed?.role !== 'auditor' && parsed?.role !== 'panelist'
            } catch {
              return true
            }
          }).length > 0
          
          return !hasLocalUser && !hasRemoteUsers
        })() && (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.5)' }}>
              No participants available
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Participant Grid - Other participants shown in smaller cards */}
      <ParticipantGrid>
        {/* This will be styled via CSS to show non-mentor participants */}
      </ParticipantGrid>
    </VideoContainer>
  )
}
